import { useLocalStorage } from '@shared/utils/useStorage';
import type { RowData } from '@tanstack/react-table';
import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useMemo } from 'react';
import { useDataProvider } from '../DataProviderContext';
import { filteredRowAggregationFn } from './aggregationFns';
import type { AggregationFn, AggregationType } from './types';

interface AggregationContext<TData extends RowData = RowData> {
  aggregateFn: AggregationFn<TData>;
  types: AggregationType[];
  showTypes: AggregationType[];
  setShowTypes: (types: AggregationType[]) => void;
  toggleShowType: (type: AggregationType) => void;
  showOnlyAggregations: boolean;
  setShowOnlyAggregations: (showOnly: boolean) => void;
  isFixed: boolean;
}

const Context = createContext<AggregationContext<any> | undefined>(undefined);

export const useAggregation = () => useContext(Context);

interface AggregationsProps {
  // Aggregation types support in the table
  types?: AggregationType[];
  // Aggregation types shown by initially when loading table
  initialTypes?: AggregationType[];
  aggregateFn?: AggregationFn<any>;
  fixed?: boolean;
  children: ReactNode;
}

export const Aggregations = ({
  aggregateFn = filteredRowAggregationFn,
  types = ['avg', 'min', 'max'],
  initialTypes = [],
  fixed = false,
  children,
}: AggregationsProps) => {
  const { showTypes, setShowTypes, toggleShowType } = useShowTypes(
    types,
    initialTypes
  );
  const [showOnlyAggregations, setShowOnlyAggregations] =
    useShowOnlyAggregations();

  return (
    <Context.Provider
      value={{
        aggregateFn,
        types,
        showTypes,
        setShowTypes,
        toggleShowType,
        showOnlyAggregations,
        setShowOnlyAggregations,
        isFixed: fixed,
      }}
    >
      {children}
    </Context.Provider>
  );
};

function getPersistKey(id: string | undefined, feature: string) {
  if (id === undefined) {
    return undefined;
  }

  return ['table', id, 'aggregations', feature].join('.');
}

const useShowTypes = (
  types: AggregationType[],
  initialTypes: AggregationType[]
) => {
  const { id } = useDataProvider();
  const persistKey = getPersistKey(id, 'showTypes');

  const [allShowTypes, setShowTypes] = useLocalStorage<AggregationType[]>(
    persistKey,
    initialTypes
  );
  const showTypes = useMemo(
    () => allShowTypes.filter((t) => types.includes(t)),
    [allShowTypes, types]
  );

  const toggleShowType = useCallback(
    (type: AggregationType) => {
      if (showTypes.includes(type)) {
        setShowTypes(showTypes.filter((t) => t !== type));
      } else {
        setShowTypes([...showTypes, type]);
      }
    },
    [showTypes, setShowTypes]
  );

  return { showTypes, setShowTypes, toggleShowType };
};

const useShowOnlyAggregations = () => {
  const { id } = useDataProvider();
  const persistKey = getPersistKey(id, 'showOnly');

  return useLocalStorage(persistKey, false);
};
